/* 
 * Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
 * Distributed under the TruVideo proprietary license. 
 */
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { authService } from '../services/authService';
import logo from '../assets/logo.png';
import toastService from '../utils/toastService';
import { CheckCircle, Lock, ArrowLeft, Eye, EyeOff, XCircle, AlertCircle, Clock } from 'lucide-react';
import { useBranding } from '../hooks/useBranding';
import { getLoginPath, normalizeAccountSlug } from '../constants/loginRoutes';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  // ?account=<slug> keeps the account login page branding and its "back to login" target
  const accountSlug = normalizeAccountSlug(searchParams.get('account'));
  const loginPath = accountSlug ? `/login/${encodeURIComponent(accountSlug)}` : getLoginPath();
  const { logoUrl: brandLogoUrl, name: brandName } = useBranding(accountSlug);

  const displayLogo = brandLogoUrl || logo;
  const displayLogoAlt = brandName ? `${brandName} Logo` : 'ServiceHub Logo';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [isValidating, setIsValidating] = useState(!!token);
  const [isTokenValid, setIsTokenValid] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (!token) {
      setError('A valid reset token is required. Please request a new password reset link.');
      setIsTokenValid(false);
      setIsValidating(false);
      return;
    }

    const validateToken = async () => {
      try {
        setIsValidating(true);
        const res = await authService.validateResetToken(token);
        if (res && res.valid) {
          setIsTokenValid(true);
        } else {
          setIsTokenValid(false);
          setError('This password reset link is invalid, has expired, or has already been used.');
        }
      } catch (err) {
        setIsTokenValid(false);
        setError('Failed to validate the reset token. The link may have expired or is invalid.');
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?])\S{8,128}$/;

  const validatePassword = (value) =>
    PASSWORD_REGEX.test(value)
      ? null
      : 'Password must be between 8 and 128 characters long and include at least one uppercase letter, one lowercase letter, one number, one special character, and no spaces.';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const errors = {};
    if (!newPassword) {
      errors.newPassword = 'Password is required';
    } else {
      const pwErr = validatePassword(newPassword);
      if (pwErr) errors.newPassword = pwErr;
    }
    if (!confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (newPassword !== confirmPassword) {
      errors.confirmPassword = 'Passwords and confirm password do not match';
    }
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      return;
    }

    if (!token) {
      setError('Missing reset token');
      return;
    }

    setLoading(true);
    try {
      await authService.resetPassword(token, newPassword, confirmPassword);
      toastService.success('Password successfully reset');
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to reset password. The link may have expired or is invalid.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden font-sans bg-base-200">
      {/* Background Mesh Gradients */}
      <div className="fixed inset-0 z-0 opacity-60">
        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-primary/10 blur-[120px] rounded-full" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-primary/5 blur-[120px] rounded-full" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60%] h-[60%] bg-secondary/5 blur-[150px] rounded-full" />
      </div>

      <div className="relative z-10 flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md flex flex-col items-center">

          {/* Brand Header */}
          <div className="text-center mb-8 animate-fade-in-down">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-2xl mb-4 shadow-xl border border-base-300 p-3 transition-transform duration-500 hover:scale-105">
              <img
                src={displayLogo}
                alt={displayLogoAlt}
                className="w-full h-full object-contain"
                onError={(e) => { e.currentTarget.src = logo; e.currentTarget.onerror = null; }}
              />
            </div>
            <h1 className="text-4xl font-black text-base-content mb-2 tracking-tighter leading-tight">
              {brandName || 'ServiceHub'}
            </h1>
            <p className="text-base-content/40 text-[10px] font-black tracking-[0.2em] uppercase">
              Service Manager Portal
            </p>
          </div>

          {/* Card */}
          <div className="w-full bg-base-100/80 backdrop-blur-2xl border border-base-300 rounded-3xl p-8 lg:p-10 shadow-[0_40px_80px_rgba(0,0,0,0.08)] relative group transition-all duration-500 hover:shadow-[0_50px_100px_rgba(0,0,0,0.12)]">
            <div className="absolute inset-0 bg-gradient-to-br from-white/40 to-transparent rounded-3xl pointer-events-none" />

            {submitted ? (
              /* ── Success State ── */
              <div className="relative z-10 flex flex-col items-center text-center space-y-6 animate-fade-in">
                <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-success" strokeWidth={2} />
                </div>
                <div>
                  <h2 className="text-xl font-black text-base-content tracking-tight uppercase mb-3">
                    Password Reset!
                  </h2>
                  <p className="text-sm text-base-content/60 leading-relaxed">
                    Your password has been successfully updated. All other active sessions have been signed out.
                  </p>
                </div>
                <div className="w-full pt-2">
                  <Link
                    to={loginPath}
                    className="flex items-center justify-center gap-2 w-full btn-premium py-4 font-black tracking-widest uppercase text-sm"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Sign In Now
                  </Link>
                </div>
              </div>
            ) : isValidating ? (
              /* ── Loading / Validating State ── */
              <div className="relative z-10 flex flex-col items-center text-center space-y-6 animate-fade-in">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <Clock className="w-8 h-8 text-primary animate-spin" style={{ animationDuration: '3s' }} strokeWidth={2} />
                </div>
                <div>
                  <h2 className="text-xl font-black text-base-content tracking-tight uppercase mb-3">
                    Validating Link
                  </h2>
                  <p className="text-sm text-base-content/60 leading-relaxed">
                    Securing your connection and checking link integrity. Please wait...
                  </p>
                </div>
              </div>
            ) : isTokenValid === false ? (
              /* ── Expired / Invalid State ── */
              <div className="relative z-10 flex flex-col items-center text-center space-y-6 animate-fade-in">
                <div className="w-16 h-16 rounded-full bg-error/10 flex items-center justify-center">
                  <XCircle className="w-8 h-8 text-error" strokeWidth={2} />
                </div>
                <div>
                  <h2 className="text-xl font-black text-base-content tracking-tight uppercase mb-3">
                    Link Expired or Invalid
                  </h2>
                  <p className="text-sm text-base-content/60 leading-relaxed">
                    {error || 'This password reset link is invalid, has expired, or has already been used.'}
                  </p>
                </div>
                <div className="w-full pt-2 space-y-3">
                  <Link
                    to="/forgot-password"
                    className="flex items-center justify-center gap-2 w-full btn-premium py-4 font-black tracking-widest uppercase text-sm"
                  >
                    Request New Link
                  </Link>
                  <Link
                    to={loginPath}
                    className="flex items-center justify-center gap-2 w-full btn btn-ghost py-4 font-black tracking-widest uppercase text-xs text-base-content/60 hover:text-base-content"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Sign In
                  </Link>
                </div>
              </div>
            ) : (
              /* ── Form State ── */
              <div className="relative z-10">
                <h2 className="text-xl font-black text-base-content mb-2 tracking-tight uppercase">
                  Reset Password
                </h2>
                <p className="text-sm text-base-content/50 mb-8 leading-relaxed">
                  Enter and confirm your new secure workspace password.
                </p>

                {error && (
                  <div className="mb-6 bg-error/10 border border-error/20 text-error px-4 py-3.5 rounded-xl text-xs font-semibold flex gap-2 items-start">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* New Password */}
                  <div className="space-y-2">
                    <label htmlFor="new-password" className="block text-[10px] font-black text-base-content/40 uppercase tracking-widest pl-1">
                      New Password *
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-base-content/30" />
                      <input
                        id="new-password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        value={newPassword}
                        onChange={(e) => { setNewPassword(e.target.value); setFieldErrors(p => ({ ...p, newPassword: null })); }}
                        onBlur={(e) => { const err = validatePassword(e.target.value); setFieldErrors(p => ({ ...p, newPassword: err })); }}
                        className={`w-full bg-base-100 border rounded-xl pl-10 pr-12 py-3.5 text-base-content text-sm placeholder-base-content/30 outline-none transition-all duration-300 focus:ring-4 ${fieldErrors.newPassword ? 'border-error focus:border-error focus:ring-error/10' : 'border-base-300 focus:border-primary focus:ring-primary/10'}`}
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        tabIndex={-1}
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-base-content/30 hover:text-base-content/60 transition-colors"
                      >
                        {!showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {fieldErrors.newPassword && (
                      <p className="text-error text-xs mt-1 font-medium">{fieldErrors.newPassword}</p>
                    )}
                  </div>

                  {/* Confirm Password */}
                  <div className="space-y-2">
                    <label htmlFor="confirm-password" className="block text-[10px] font-black text-base-content/40 uppercase tracking-widest pl-1">
                      Confirm Password *
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-base-content/30" />
                      <input
                        id="confirm-password"
                        type={showConfirmPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChange={(e) => { setConfirmPassword(e.target.value); setFieldErrors(p => ({ ...p, confirmPassword: null })); }}
                        onBlur={(e) => { if (e.target.value !== newPassword) setFieldErrors(p => ({ ...p, confirmPassword: 'Password and confirm password do not match' })); }}
                        className={`w-full bg-base-100 border rounded-xl pl-10 pr-12 py-3.5 text-base-content text-sm placeholder-base-content/30 outline-none transition-all duration-300 focus:ring-4 ${fieldErrors.confirmPassword ? 'border-error focus:border-error focus:ring-error/10' : 'border-base-300 focus:border-primary focus:ring-primary/10'}`}
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        tabIndex={-1}
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-base-content/30 hover:text-base-content/60 transition-colors"
                      >
                        {!showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {fieldErrors.confirmPassword && (
                      <p className="text-error text-xs mt-1 font-medium">{fieldErrors.confirmPassword}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    id="reset-password-submit"
                    disabled={loading}
                    className="w-full btn-premium py-4 font-black tracking-widest uppercase text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Updating Password...' : 'Reset Password'}
                  </button>
                </form>
              </div>
            )}
          </div>

          <p className="mt-10 text-base-content/20 text-[10px] font-black uppercase tracking-[0.25em]">
            &copy; {new Date().getFullYear()} ServiceHub • ENTERPRISE SECURE
          </p>
        </div>
      </div>
    </div>
  );
}
